import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';
import { applyTemplateNow, deleteEntries } from '../../utils/templateApply';

// The Log-page daily checklist — a vertical "queue" of what still needs logging
// today. One card in focus with a half-peek above and below; items fill green as
// they're logged (metric/category items tick themselves from your entries, custom
// items on a tap) and the list gently advances to the next. Progress rides in the
// count + dots; when everything's in, it collapses to a small "all done" note.
//
// Done-state is resolved by the backend (services/checklist.py) from the day's
// actual entries — this component just renders it and animates the transitions.

const todayStr = () => localToday();

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2"
       strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DailyChecklist = ({ refreshKey = 0, defaultDate, onQuickLog, onLogged }) => {
  const day = defaultDate || todayStr();
  const [data, setData] = useState(null);
  const [flashIds, setFlashIds] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);

  const windowRef = useRef(null);
  const prevDoneRef = useRef(null);          // null until the first load lands
  const lastManualScrollRef = useRef(0);
  const scrollTimerRef = useRef(null);

  const activeIndex = data ? data.items.findIndex((i) => !i.done) : -1;

  const scrollToActive = useCallback((smooth) => {
    const win = windowRef.current;
    if (!win || !data) return;
    const idx = data.items.findIndex((i) => !i.done);
    if (idx < 0) return;
    // Gentle guard: never yank the view if the user just scrolled by hand.
    if (smooth && Date.now() - lastManualScrollRef.current < 1500) return;
    const el = win.children[idx];
    if (!el) return;
    const top = el.offsetTop - (win.clientHeight / 2 - el.clientHeight / 2);
    win.scrollTo({ top, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
  }, [data]);

  // load() reaches the latest scrollToActive through this ref, NOT a dependency.
  // scrollToActive depends on `data`; if load depended on it directly, every
  // load()'s setData would rebuild load, which re-fired the load effect below —
  // an infinite /api/checklist/today fetch loop. The ref keeps load's identity
  // stable across data changes so the effect only runs on day/refreshKey.
  const scrollToActiveRef = useRef(scrollToActive);
  scrollToActiveRef.current = scrollToActive;

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/checklist/today', {
        params: { target_date: day },
      });
      const fresh = res.data;
      const doneIds = new Set(fresh.items.filter((i) => i.done).map((i) => i.id));
      const prev = prevDoneRef.current;

      if (prev) {
        // Flash the items that became done since the last look, then let the
        // green sit ~0.5s (the reward) before gently advancing to the next.
        const newlyDone = [...doneIds].filter((id) => !prev.has(id));
        if (newlyDone.length) {
          setFlashIds(new Set(newlyDone));
          setTimeout(() => setFlashIds(new Set()), 700);
          clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = setTimeout(() => scrollToActiveRef.current(true), 520);
        }
      }
      prevDoneRef.current = doneIds;
      setData(fresh);
    } catch {
      // Silent: the strip just doesn't render if the fetch fails (e.g. before
      // the migration runs), so it can never break the Log page.
    }
  }, [day]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Center the first pending item once data first arrives (no smooth jump).
  useEffect(() => {
    if (data) scrollToActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data === null]);

  const onWindowScroll = () => { lastManualScrollRef.current = Date.now(); };

  const tap = async (item) => {
    // Auto-detected items are locked done by the data; only manual ticks toggle.
    if (item.auto_done || busyId) return;
    const nextDone = !item.manual;
    setBusyId(item.id);
    // Optimistic: flip it now so the tap feels instant.
    setData((prev) => prev && {
      ...prev,
      items: prev.items.map((i) =>
        i.id === item.id ? { ...i, done: nextDone, manual: nextDone } : i),
    });
    if (nextDone) {
      setFlashIds(new Set([item.id]));
      setTimeout(() => setFlashIds(new Set()), 700);
    }
    try {
      const res = await apiClient.post('/api/checklist/tick', {
        item_id: item.id, done: nextDone, date: day,
      });
      prevDoneRef.current = new Set(res.data.items.filter((i) => i.done).map((i) => i.id));
      setData(res.data);
      if (nextDone) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = setTimeout(() => scrollToActive(true), 520);
      }
    } catch {
      load();   // reconcile with the server on failure
    } finally {
      setBusyId(null);
    }
  };

  // A template row is a one-tap quick-log: apply the routine, tick green, and offer
  // an Undo. Distinct from tap() (a manual tick) — here the tap creates real entries.
  const applyTemplateRow = async (item) => {
    if (busyId) return;
    setBusyId(item.id);
    // Optimistic tick so the tap feels instant.
    setData((prev) => prev && {
      ...prev,
      items: prev.items.map((i) =>
        i.id === item.id ? { ...i, done: true, auto_done: true } : i),
    });
    setFlashIds(new Set([item.id]));
    setTimeout(() => setFlashIds(new Set()), 700);
    try {
      const t = { id: item.template_id, name: item.label, items: item.items || [] };
      const { count, ids, name } = await applyTemplateNow(t, day);
      if (!count) {
        toast('That routine has nothing to log yet.');
        load();
        return;
      }
      toast((tt) => (
        <span className="flex items-center gap-3">
          <span>Logged {count} from “{name}”.</span>
          <button
            type="button"
            onClick={async () => {
              toast.dismiss(tt.id);
              await deleteEntries(ids);
              if (onLogged) onLogged();
            }}
            className="font-semibold text-primary-700 dark:text-primary-300 underline"
          >
            Undo
          </button>
        </span>
      ), { duration: 6000 });
      if (onLogged) onLogged();   // bump → history + checklist reconcile
    } catch {
      toast.error('Could not log that routine. Please try again.');
      load();
    } finally {
      setBusyId(null);
    }
  };

  // The always-available entry point to the routine picker (replaces the old 💊 pill).
  const quickLogRow = onQuickLog ? (
    <button
      type="button"
      onClick={onQuickLog}
      className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-surface-border dark:border-surface-dark-border px-3 py-2 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-surface-dark-raised"
    >
      <span className="text-base leading-none">＋</span> Quick-log a routine
    </button>
  ) : null;

  if (!data || data.total === 0) {
    return quickLogRow ? (
      <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card px-4 py-3">
        {quickLogRow}
      </div>
    ) : null;
  }

  const { items, done, total, all_complete } = data;

  const maskStyle = {
    WebkitMaskImage:
      'linear-gradient(180deg, transparent 0, #000 36px, #000 calc(100% - 36px), transparent 100%)',
    maskImage:
      'linear-gradient(180deg, transparent 0, #000 36px, #000 calc(100% - 36px), transparent 100%)',
  };

  return (
    <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card px-4 py-3">
      <style>{`
        .vgdc-window { scrollbar-width: none; -ms-overflow-style: none; }
        .vgdc-window::-webkit-scrollbar { display: none; }
      `}</style>

      {/* progress header */}
      <div className="flex items-center gap-3 mb-1.5">
        <span className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Today
        </span>
        <span className="flex items-center gap-1 mr-auto">
          {items.map((i) => (
            <span
              key={i.id}
              aria-hidden="true"
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                i.done ? 'bg-good' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </span>
        <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
          <span className="text-good-strong dark:text-good">{done}</span>/{total}
        </span>
      </div>

      {all_complete ? (
        <div className="flex items-center gap-3 py-3 px-1">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-good text-white">
            <Check />
          </span>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-white">All logged for today</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Nice — everything on your list is in.</p>
          </div>
        </div>
      ) : (
        <div
          ref={windowRef}
          onScroll={onWindowScroll}
          className="vgdc-window"
          style={{
            height: '132px',
            overflowY: 'auto',
            scrollSnapType: 'y proximity',
            padding: '42px 0',
            ...maskStyle,
          }}
        >
          {items.map((item, idx) => {
            const isActive = idx === activeIndex;
            const isFlash = flashIds.has(item.id);
            const tappable = !item.auto_done;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => (item.kind === 'template' ? applyTemplateRow(item) : tap(item))}
                disabled={!tappable || busyId === item.id}
                style={{ scrollSnapAlign: 'center' }}
                className={`w-full text-left flex items-center gap-3 my-1 px-3.5 py-1.5 rounded-xl border transition-all duration-300 ${
                  item.done
                    ? 'bg-good/10 border-good/40'
                    : isActive
                      ? 'bg-gray-50 dark:bg-surface-dark-raised border-primary-400/50 shadow-sm'
                      : 'bg-gray-50 dark:bg-surface-dark-raised border-surface-border dark:border-surface-dark-border'
                } ${isFlash ? 'ring-2 ring-good/60' : ''} ${
                  tappable && !item.done ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                    item.done
                      ? 'bg-good border-good text-white'
                      : 'border-gray-300 dark:border-gray-600 text-transparent'
                  }`}
                >
                  <Check />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-semibold text-gray-900 dark:text-white truncate">
                    {item.label}
                  </span>
                  {item.meta && (
                    <span className="block text-sm text-gray-500 dark:text-gray-400 truncate">
                      {item.meta}
                    </span>
                  )}
                </span>
                {!item.done && (
                  <span
                    className={`shrink-0 text-sm font-semibold uppercase tracking-wide rounded-full border px-2 py-0.5 ${
                      item.auto || item.kind === 'template'
                        ? 'text-primary-600 dark:text-primary-400 border-primary-400/50'
                        : 'text-gray-500 dark:text-gray-400 border-surface-border dark:border-surface-dark-border'
                    }`}
                  >
                    {item.auto ? 'Auto' : item.kind === 'template' ? 'Log' : item.kind === 'custom' ? 'Tap' : 'Log'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {quickLogRow}
    </div>
  );
};

export default DailyChecklist;
